import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  Play, 
  Eye, 
  FileText,
  Download,
  X,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentExecutionDialog = ({ 
  isOpen, 
  onClose, 
  agent, 
  companyId, 
  onExecute 
}) => {
  const [activeTab, setActiveTab] = useState('excel');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [convertedData, setConvertedData] = useState(null);
  const [jsonInput, setJsonInput] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setIsConverting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/companies/${companyId}/agents/${agent.id}/convert-excel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setConvertedData(response.data);
      setJsonInput(JSON.stringify(response.data.suggested_json, null, 2));
      setActiveTab('json');
      toast.success('Archivo convertido exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al convertir archivo');
    } finally {
      setIsConverting(false);
    }
  }, [companyId, agent?.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1
  });

  const handleExecute = async () => {
    let inputData;
    try {
      inputData = JSON.parse(jsonInput);
    } catch (error) {
      toast.error('El JSON no es válido');
      return;
    }

    setIsExecuting(true);
    try {
      await onExecute(agent.id, inputData);
      onClose();
    } catch (error) {
      // Error is handled by parent component
    } finally {
      setIsExecuting(false);
    }
  };

  const downloadTemplate = () => {
    // Create a sample template based on agent schema
    const schema = agent.input_schema || {};
    const sampleData = [];
    
    // Generate sample rows based on common patterns
    for (let i = 1; i <= 5; i++) {
      const row = {
        id: i,
        name: `Sample Item ${i}`,
        value: Math.floor(Math.random() * 1000) + 100,
        date: new Date().toISOString().split('T')[0],
        category: ['A', 'B', 'C'][i % 3],
        description: `Sample description for item ${i}`
      };
      sampleData.push(row);
    }

    // Convert to CSV
    const headers = Object.keys(sampleData[0]);
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${agent.name}_template.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const resetForm = () => {
    setUploadedFile(null);
    setConvertedData(null);
    setJsonInput(JSON.stringify({
      query: `Ejecutar análisis con ${agent?.name}`,
      parameters: {},
      filters: {}
    }, null, 2));
    setActiveTab('excel');
  };

  React.useEffect(() => {
    if (agent && isOpen) {
      resetForm();
    }
  }, [agent, isOpen]);

  if (!agent) return null;

  // ALL AGENTS use Agent 00 - no direct data upload for any agent type
  const agentTypeLabel = agent.agent_type === 'functional' ? 'Funcional' : 
                         agent.agent_type === 'premium' ? 'Premium' : 
                         agent.agent_type === 'c_level' ? 'C-Level' : agent.agent_type;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-dark border-blue-500/20 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-blue-400" />
              Ejecutar Agente: {agent.name}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Agent Info */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <p className="text-sm text-slate-300 mb-3">{agent.description}</p>
              <div className="flex gap-2">
                <Badge className="badge-info">{agent.module_type}</Badge>
                <Badge className="badge-success">Habilitado</Badge>
                <Badge className="badge-warning">{agentTypeLabel}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* ALL agents now use Agent 00 for data ingestion */}
          <Card className="bg-blue-900/20 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <FileSpreadsheet className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-300 mb-2">
                    Ingesta Centralizada de Datos - Agente 00
                  </h3>
                  <p className="text-sm text-slate-300 mb-4">
                    Todos los agentes ahora utilizan el sistema de <strong>Agente 00</strong> para ingesta centralizada de datos. 
                    Este sistema permite subir archivos (CSV, Excel, PDF) que se procesan automáticamente y ejecutan toda la cadena de agentes: 
                    Funcionales → Premium → C-Level → Insights.
                  </p>
                  <div className="bg-slate-800/50 p-3 rounded-lg mb-4">
                    <p className="text-xs text-slate-400 mb-2">🔹 <strong>Ventajas del Agente 00:</strong></p>
                    <ul className="text-xs text-slate-400 space-y-1 ml-4">
                      <li>• Ingesta centralizada de datos por módulo</li>
                      <li>• Ejecución automática de todos los agentes (Funcionales, Premium, C-Level)</li>
                      <li>• Generación automática de insights</li>
                      <li>• Seguimiento de errores y éxitos</li>
                      <li>• Distribución inteligente de datos según input_schema</li>
                    </ul>
                  </div>
                  <p className="text-sm text-amber-300 mb-4">
                    ℹ️ Para ejecutar este agente <strong>{agentTypeLabel}</strong>, ve a la sección <strong>"Ingesta de Datos"</strong> en el menú lateral y sube los archivos del módulo.
                  </p>
                  <Button 
                    onClick={onClose}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Ir a Ingesta de Datos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentExecutionDialog;
